import { useMemo } from 'react'

import { useCubeReportPageFiltersContext } from 'cube'
import {
  CONVERSATION_FACTS_DIMENSIONS,
  ROUTING_MEASURES,
} from 'cube/constants/QueryConstants'
import { MedianFirstResponseQuery } from 'cube/queries'
import { isNil } from 'ramda'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import {
  type RoutingKPIMetric,
  resolveCubeKPIResults,
  resolveKPIScore,
  resolveTotalRecords,
} from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

import { KPI_SCORES } from './FirstResponse.constants'

/***
 * Hook that cube to fetch response rate data
 *
 * @returns {RoutingKPIMetric}
 */
export const useFirstResponse = (): RoutingKPIMetric => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()

  const cubeQuery = new CubeQueryBuilder({ query: MedianFirstResponseQuery })
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .withFilters(pageFilters)
    .build()

  const { data, isLoading, isError } = useCubeQuery({
    query: cubeQuery,
  })

  const medianFirstResponse = useMemo(
    () => resolveCubeKPIResults(data, MedianFirstResponseQuery.queryName ?? ''),
    [data]
  )

  const score = useMemo(
    () => resolveKPIScore(medianFirstResponse, KPI_SCORES),
    [medianFirstResponse]
  )

  const metric = useMemo(
    () => medianFirstResponse ?? null,
    [medianFirstResponse]
  )

  const hasNoData = useMemo(
    () => !isLoading && isNil(medianFirstResponse),
    [isLoading, medianFirstResponse]
  )

  const totalRecords = useMemo(
    () => resolveTotalRecords(data, ROUTING_MEASURES.RESPONSE_COUNT),
    [data]
  )

  const totalRequests = useMemo(
    () =>
      resolveTotalRecords(
        data,
        ROUTING_MEASURES.ROUTING_REQUEST_TO_TEAMMATE_ROUTED_IN
      ),
    [data]
  )

  return useMemo(
    () => ({
      score,
      metric,
      isLoading,
      isError,
      hasNoData,
      totalRecords,
      totalRequests,
    }),
    [score, metric, isLoading, isError, hasNoData, totalRecords, totalRequests]
  )
}

export default useFirstResponse
